import { ReactNode, useMemo } from 'react';
import {
  GenericOption,
  VariableOption,
  VariableCompatibility,
} from 'pages/AutomationEngine/types';
import { VariableSelectWithAdd, type VariableOptionGroup } from '.';
import { useTranslation } from 'react-i18next';

type GroupedVariableSelectWithAddProps<T extends boolean> = {
  isMulti?: T;
  value?: (T extends true ? GenericOption[] : GenericOption) | null;
  onChange: (value: T extends true ? VariableOption[] : VariableOption) => void;
  options: Array<VariableOption | VariableOptionGroup>;
  allowedDataTypes?: VariableCompatibility;
  allowedSubtype?: GenericOption;
  showCompatibilityNotice?: boolean;
  label?: string | null;
  labelInfo?: string;
  labelInfoPlacement?: string;
  labelInfoMaxWidth?: number;
  placeholder?: string;
  margin?: boolean | string;
  error?: boolean;
  validate?:
    | { message: string | string[] | null; showMessage?: boolean }
    | undefined;
  menuInline?: boolean;
  menuLeftButton?: ReactNode;
  noOptionsMessage?: string;
  portal?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  inModal?: boolean;
  'data-qa'?: string;
  dataQa?: string;
  'data-field-type'?: string;
  'data-field-id'?: string;
  onMenuOpen?: () => void;
  includeGroupPlaceholder?: boolean;
};

const isGroup = (
  opt: VariableOption | VariableOptionGroup
): opt is VariableOptionGroup =>
  Array.isArray((opt as VariableOptionGroup)?.options);

export const GroupedVariableSelectWithAdd = <T extends boolean = false>({
  isMulti = false as T,
  value,
  onChange,
  allowedDataTypes,
  allowedSubtype,
  showCompatibilityNotice = true,
  label,
  placeholder,
  options: optionsProp,
  ...rest
}: GroupedVariableSelectWithAddProps<T>) => {
  const { t } = useTranslation();

  // Item variables are pulled out of whatever was passed - flat options or
  // groups, such as the math builder's - into a group of their own at the end.
  const options: Array<VariableOption | VariableOptionGroup> = useMemo(() => {
    const isItemVariable = (opt: VariableOption) =>
      !!opt?.meta?.isArrayItemVariable;

    const itemVars = optionsProp.flatMap((opt) =>
      isGroup(opt)
        ? opt.options.filter(isItemVariable)
        : isItemVariable(opt)
          ? [opt]
          : []
    );

    if (!itemVars.length) {
      return optionsProp;
    }

    const groups: Array<VariableOptionGroup> = [];
    const ungrouped: Array<VariableOption> = [];

    optionsProp.forEach((opt) => {
      if (isGroup(opt)) {
        const vars = opt.options.filter((o) => !isItemVariable(o));
        if (vars.length) {
          groups.push({ ...opt, options: vars });
        }
      } else if (!isItemVariable(opt)) {
        ungrouped.push(opt);
      }
    });

    return [
      // a select can't mix grouped and ungrouped options, so loose ones go
      // under a heading of their own
      ...(ungrouped.length
        ? [{ label: t('Variables'), options: ungrouped }]
        : []),
      ...groups,
      { label: t('Item Variables'), options: itemVars },
    ];
  }, [optionsProp, t]);

  return (
    <VariableSelectWithAdd
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      label={label}
      isMulti={isMulti}
      allowedDataTypes={allowedDataTypes}
      allowedSubtype={allowedSubtype}
      showCompatibilityNotice={showCompatibilityNotice}
      {...rest}
      options={options}
      includeGroupPlaceholder={false}
    />
  );
};
